import { EMAIL_CANVAS_MAX_WIDTH } from "../constants/emails";
const WIDTHLESS_IMG_REGEX = /<img(?![^>]*?\swidth=)([^>]*?)(\/?)>/g;
const WIDTHLESS_IMG_DEFAULT_WIDTH = EMAIL_CANVAS_MAX_WIDTH - 48;
const EBC_COL_OPEN_REGEX = /<div class="ebc-col" style="[^"]*max-width:(\d+)px[^"]*"[^>]*>/g;
const WAYPOINT_BUTTON_REGEX = /<a\s+href="([^"]+)"\s+style="([^"]+)"\s+target="_blank"><span><!--\[if mso\]><i [^>]*hidden>&nbsp;<\/i><!\[endif\]--><\/span><span>([^<]+)<\/span><span><!--\[if mso\]><i [^>]*hidden>&nbsp;<\/i><!\[endif\]--><\/span><\/a>/g;
const SOCIAL_ANCHOR_REGEX = /<a\s+[^>]*?data-social-platform="[^"]+"[^>]*?\shref="([^"]+)"[^>]*>\s*<table\b[\s\S]*?<td[^>]*background-color\s*:\s*([^;"\s]+)[\s\S]*?<img[^>]*\ssrc="([^"]+)"[\s\S]*?alt="([^"]*)"[\s\S]*?<\/table>\s*<\/a>/g;
const SOCIALS_SECTION_REGEX = /<div\s+style="([^"]*background-color:[^"]*)"[^>]*>\s*(<div\s+[^>]*?data-block-type="socials"[^>]*>)([\s\S]*?<\/div>)\s*<\/div>\s*<\/div>|(<div\s+[^>]*?data-block-type="socials"[^>]*>)([\s\S]*?<\/div>)\s*<\/div>/g;
const CANVAS_INNER_TD_REGEX = /<td style="word-wrap: break-word; overflow-wrap: break-word; width: 100%;">/;
const DIVIDER_HR_REGEX = /<hr\s+style="([^"]*)"\s*\/?>/g;
const DIVIDER_LINE_REGEX = /^([\d.]+)px\s+solid\s+(.+)$/;
const SPACER_DIV_REGEX = /<div\s+data-block-type="spacer"\s+style="([^"]*)">\s*&nbsp;\s*<\/div>/gi;
const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/i;
const MSO_OPT_OUT_OPEN = "<!--[if !mso]><!-->";
const MSO_OPT_OUT_CLOSE = "<!--<![endif]-->";
const VML_BUTTON_AVG_GLYPH_FACTOR = 0.6;
const XHTML_DOCTYPE = '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">';
const OUTLOOK_HTML_OPEN = '<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">';
const OUTLOOK_HEAD_EXTRAS = `
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta name="x-apple-disable-message-reformatting" />
<!--[if !mso]><!--><meta http-equiv="X-UA-Compatible" content="IE=edge" /><!--<![endif]-->
<!--[if gte mso 9]>
<xml>
  <o:OfficeDocumentSettings>
    <o:AllowPNG/>
    <o:PixelsPerInch>96</o:PixelsPerInch>
  </o:OfficeDocumentSettings>
</xml>
<![endif]-->
<!--[if mso]>
<style type="text/css">
  table, td { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
  div, p, td, a, span { mso-line-height-rule: exactly; }
</style>
<![endif]-->
<style type="text/css" data-embed>
  a[x-apple-data-detectors],
  a[x-apple-data-detectors] * {
    color: inherit !important;
    text-decoration: none !important;
    font-size: inherit !important;
    font-family: inherit !important;
    font-weight: inherit !important;
    line-height: inherit !important;
    -webkit-text-fill-color: inherit !important;
  }
</style>`;
const MSO_CANVAS_GHOST_OPEN = `<!--[if mso | IE]>
<table role="presentation" align="center" border="0" cellpadding="0" cellspacing="0" width="${EMAIL_CANVAS_MAX_WIDTH}" style="width:${EMAIL_CANVAS_MAX_WIDTH}px;">
<tr><td align="center" valign="top">
<![endif]-->`;
const MSO_CANVAS_GHOST_CLOSE = `<!--[if mso | IE]>
</td></tr>
</table>
<![endif]-->`;
const WRAPPED_FOR_OUTLOOK_SENTINEL = 'xmlns:v="urn:schemas-microsoft-com:vml"';
export const hasOutlookWrap = (html) => html.includes(WRAPPED_FOR_OUTLOOK_SENTINEL);
export const hasUnscopedDataDetectorStyle = (html) => html.includes("#MessageViewBody");
// Extracts the value of an inline CSS property (e.g. `background-color`) from a style attribute string.
const readCssValue = (style, property) => {
    const re = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, "i");
    return style.match(re)?.[1].trim();
};
// Parses px from the "T R B L" padding shorthand the block renderers emit.
export const parsePadding = (styleString) => {
    const match = styleString.match(/padding:(\d+)px (\d+)px (\d+)px (\d+)px/);
    if (!match) {
        return { top: 0, right: 0, bottom: 0, left: 0 };
    }
    return { top: Number(match[1]), right: Number(match[2]), bottom: Number(match[3]), left: Number(match[4]) };
};
// Returns the index right after the matching `</tag>` whose body starts at `bodyStart`; -1 if depth doesn't balance.
const findMatchingClose = (html, tag, bodyStart) => {
    const openTok = `<${tag}`;
    const closeTok = `</${tag}>`;
    let cursor = bodyStart;
    let depth = 1;
    while (depth > 0) {
        const nextOpen = html.indexOf(openTok, cursor);
        const nextClose = html.indexOf(closeTok, cursor);
        if (nextClose === -1) {
            return -1;
        }
        if (nextOpen !== -1 && nextOpen < nextClose) {
            depth++;
            cursor = nextOpen + openTok.length;
        }
        else {
            depth--;
            cursor = nextClose + closeTok.length;
        }
    }
    return cursor;
};
// Returns `[start, end)` ranges of every top-level `<div>` inside `[from, to)`; `[]` on malformed input.
const findTopLevelDivs = (html, from, to) => {
    const sections = [];
    let scan = from;
    while (scan < to) {
        const nextOpen = html.indexOf("<div", scan);
        if (nextOpen === -1 || nextOpen >= to) {
            break;
        }
        const tagEnd = html.indexOf(">", nextOpen);
        if (tagEnd === -1 || tagEnd >= to) {
            return [];
        }
        const childEnd = findMatchingClose(html, "div", tagEnd + 1);
        if (childEnd === -1 || childEnd > to) {
            return [];
        }
        sections.push({ start: nextOpen, end: childEnd });
        scan = childEnd;
    }
    return sections;
};
// Replaces each Waypoint button with a VML `<v:roundrect>` for Outlook and keeps the original anchor for every other client.
const wrapButtonsForOutlook = (html) => {
    return html.replace(WAYPOINT_BUTTON_REGEX, (_match, href, style, text) => {
        const bgColor = readCssValue(style, "background-color") ?? "#5555E7";
        const textColor = readCssValue(style, "color") ?? "#FFFFFF";
        const borderRadius = parseInt(readCssValue(style, "border-radius") ?? "4", 10);
        const fontSize = parseInt(readCssValue(style, "font-size") ?? "16", 10);
        const fontWeightRaw = readCssValue(style, "font-weight") ?? "normal";
        const fontWeight = /^bold$/i.test(fontWeightRaw) || Number(fontWeightRaw) >= 500 ? "bold" : "normal";
        const padding = readCssValue(style, "padding") ?? "12px 20px";
        const paddingMatch = padding.match(/(\d+)px\s+(\d+)px/);
        const paddingY = paddingMatch ? parseInt(paddingMatch[1], 10) : 12;
        const paddingX = paddingMatch ? parseInt(paddingMatch[2], 10) : 20;
        const height = paddingY * 2 + fontSize;
        const width = paddingX * 2 + Math.ceil(text.length * fontSize * VML_BUTTON_AVG_GLYPH_FACTOR);
        const arcsize = Math.round((borderRadius / Math.min(width, height)) * 100);
        return (`<!--[if mso]>` +
            `<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" ` +
            `href="${href}" style="height:${height}px;v-text-anchor:middle;width:${width}px;" ` +
            `arcsize="${arcsize}%" stroke="f" fillcolor="${bgColor}">` +
            `<w:anchorlock/>` +
            `<center style="color:${textColor};font-family:Helvetica,Arial,sans-serif;font-size:${fontSize}px;font-weight:${fontWeight};">${text}</center>` +
            `</v:roundrect>` +
            `<![endif]-->` +
            `<!--[if !mso]><!-->` +
            `<a href="${href}" style="${style}" target="_blank">${text}</a>` +
            `<!--<![endif]-->`);
    });
};
// Wraps each socials block in an `[if mso]` ghost so Word lays icons horizontally as real `<td>` cells; swallows a colored parent `<div>` when present so the section bg paints reliably.
const wrapSocialsBlockForOutlook = (html) => html.replace(SOCIALS_SECTION_REGEX, (match, parentStyle, openA, innerA, openB, innerB, offset) => {
    const blockOpen = (openA ?? openB);
    const inner = (innerA ?? innerB);
    if (inner.includes("<!--[if mso]>")) {
        return match;
    }
    if (html.slice(Math.max(0, offset - 200), offset).includes("<!--[if !mso]><!-->")) {
        return match;
    }
    const cells = buildSocialIconCells(inner);
    if (cells === null) {
        return match;
    }
    if (parentStyle) {
        const bgColor = readCssValue(parentStyle, "background-color") ?? "#000000";
        const padding = readCssValue(parentStyle, "padding") ?? "16px 24px";
        return `${buildBgPaintMsoWrap(bgColor, padding, cells)}<!--[if !mso]><!-->${match}<!--<![endif]-->`;
    }
    return `${blockOpen}${buildInnerOnlyMsoWrap(cells)}<!--[if !mso]><!-->${inner}<!--<![endif]--></div>`;
});
// Parses anchors out of a socials block's inner HTML into Word-safe icon `<td>` cells; null when no anchors match.
const buildSocialIconCells = (innerHtml) => {
    SOCIAL_ANCHOR_REGEX.lastIndex = 0;
    const cells = [];
    let m;
    while ((m = SOCIAL_ANCHOR_REGEX.exec(innerHtml)) !== null) {
        cells.push(buildSocialIconCell(m[1], m[2], m[3], m[4]));
    }
    return cells.length === 0 ? null : cells.join("");
};
// One Word-safe icon cell — `bgcolor` attr, explicit 40x40. The image is block + `vertical-align:middle`
// and the cell uses `line-height:0` so Word doesn't baseline-align the icon and clip its top.
const buildSocialIconCell = (href, bgColor, imgSrc, imgAlt) => `<td valign="middle" style="padding:0 8px;font-size:0;line-height:0;">` +
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>` +
    `<td bgcolor="${bgColor}" align="center" valign="middle" width="40" height="40" ` +
    `style="background-color:${bgColor};width:40px;height:40px;line-height:0;mso-line-height-rule:exactly;text-align:center;border-radius:6px;font-size:0;">` +
    `<a href="${href}" style="text-decoration:none;display:block;line-height:0;font-size:0;">` +
    `<img src="${imgSrc}" width="40" height="40" alt="${imgAlt}" border="0" style="display:block;border:0;outline:none;vertical-align:middle;"></a>` +
    `</td></tr></table></td>`;
// Centered inner table that holds the icon `<td>` cells, shared by both MSO ghost variants.
const buildSocialIconRowTable = (cells) => `<table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>${cells}</tr></table>`;
// MSO ghost for socials with no colored parent — just the centered icon row.
const buildInnerOnlyMsoWrap = (cells) => `<!--[if mso]>${buildSocialIconRowTable(cells)}<![endif]-->`;
// MSO ghost for socials with a colored parent — full-width `<table bgcolor>` shell wrapping the icon row.
const buildBgPaintMsoWrap = (bgColor, padding, cells) => `<!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `bgcolor="${bgColor}" style="background-color:${bgColor};border-collapse:collapse;"><tr>` +
    `<td align="center" valign="middle" bgcolor="${bgColor}" style="background-color:${bgColor};padding:${padding};">` +
    `${buildSocialIconRowTable(cells)}` +
    `</td></tr></table><![endif]-->`;
// Reads a Divider block's `border-top:<N>px solid <color>`; null for any other `<hr>` (e.g. custom HTML blocks).
const parseDividerLine = (style) => {
    const match = readCssValue(style, "border-top")?.match(DIVIDER_LINE_REGEX);
    const height = match ? Math.round(Number(match[1])) : NaN;
    if (!match || !Number.isFinite(height)) {
        return null;
    }
    return { lineColor: match[2].trim(), height: Math.max(height, 1) };
};
const isDividerAlreadyWrapped = (html, offset) => html.slice(Math.max(0, offset - MSO_OPT_OUT_OPEN.length), offset).endsWith(MSO_OPT_OUT_OPEN);
// Paints the line as a `<td>` background because Word ignores CSS borders on `<hr>`.
// `transparent` is left without a color so the enclosing section's background shows through.
const buildMsoDividerCell = (lineColor, height) => {
    const isTransparent = lineColor.toLowerCase() === "transparent";
    const bgAttr = isTransparent ? "" : ` bgcolor="${lineColor}"`;
    const bgStyle = isTransparent ? "" : `background-color:${lineColor};`;
    return (`<!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
        `style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;"><tr>` +
        `<td height="${height}"${bgAttr} style="${bgStyle}height:${height}px;line-height:${height}px;font-size:1px;">&nbsp;</td>` +
        `</tr></table><![endif]-->`);
};
// True when the body still has a Divider `<hr>` Word would paint as its own grey rule.
export const hasUnwrappedDividers = (html) => {
    DIVIDER_HR_REGEX.lastIndex = 0;
    let match;
    while ((match = DIVIDER_HR_REGEX.exec(html)) !== null) {
        if (parseDividerLine(match[1]) && !isDividerAlreadyWrapped(html, match.index)) {
            return true;
        }
    }
    return false;
};
// Swaps each Divider `<hr>` for an MSO table cell and keeps the original `<hr>` for every other client.
const wrapDividersForOutlook = (html) => {
    DIVIDER_HR_REGEX.lastIndex = 0;
    return html.replace(DIVIDER_HR_REGEX, (match, style, offset) => {
        const line = parseDividerLine(style);
        if (!line || isDividerAlreadyWrapped(html, offset)) {
            return match;
        }
        return `${buildMsoDividerCell(line.lineColor, line.height)}${MSO_OPT_OUT_OPEN}${match}${MSO_OPT_OUT_CLOSE}`;
    });
};
const wrapSpacersForOutlook = (html) => html.replace(SPACER_DIV_REGEX, (match, style) => {
    const backgroundColor = readCssValue(style, "background-color");
    const height = Math.round(Number.parseFloat(readCssValue(style, "height") ?? ""));
    if (!backgroundColor || !HEX_COLOR_REGEX.test(backgroundColor) || !Number.isFinite(height) || height < 0) {
        return match;
    }
    const outlookSpacer = `<!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
        `style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;"><tr>` +
        `<td height="${height}" bgcolor="${backgroundColor}" ` +
        `style="background-color:${backgroundColor};height:${height}px;line-height:${height}px;font-size:1px;">` +
        `&nbsp;</td></tr></table><![endif]-->`;
    return `${outlookSpacer}${MSO_OPT_OUT_OPEN}${match}${MSO_OPT_OUT_CLOSE}`;
});
const calculateImagePadding = (html, imgOffset, searchStart, searchEnd) => {
    const wrapper = findTopLevelDivs(html, searchStart, searchEnd).find((div) => imgOffset >= div.start && imgOffset < div.end);
    if (!wrapper) {
        return 0;
    }
    const openTagEnd = html.indexOf(">", wrapper.start);
    const { left, right } = parsePadding(html.slice(wrapper.start, openTagEnd));
    return left + right + calculateImagePadding(html, imgOffset, openTagEnd + 1, wrapper.end - "</div>".length);
};
// Injects `width="N"` on every widthless `<img>` — column max-width when inside an `ebc-col`,
const clampWidthlessImagesToContext = (html) => {
    EBC_COL_OPEN_REGEX.lastIndex = 0;
    const colRanges = [];
    let openMatch;
    while ((openMatch = EBC_COL_OPEN_REGEX.exec(html)) !== null) {
        const colStart = openMatch.index;
        const colEnd = findMatchingClose(html, "div", colStart + openMatch[0].length);
        if (colEnd === -1) {
            continue;
        }
        colRanges.push({ start: colStart, end: colEnd, width: parseInt(openMatch[1], 10) });
    }
    const tdMatch = html.match(CANVAS_INNER_TD_REGEX);
    const canvasInnerStart = tdMatch?.index !== undefined ? tdMatch.index + tdMatch[0].length : -1;
    const tdClose = canvasInnerStart === -1 ? -1 : findMatchingClose(html, "td", canvasInnerStart);
    const canvasInnerEnd = tdClose === -1 ? -1 : tdClose - "</td>".length;
    const canvasResolved = canvasInnerStart !== -1 && canvasInnerEnd > canvasInnerStart;
    return html.replace(WIDTHLESS_IMG_REGEX, (_match, attrs, slash, offset) => {
        const enclosingColumn = colRanges.find((range) => offset >= range.start && offset < range.end);
        if (enclosingColumn) {
            return `<img${attrs} width="${enclosingColumn.width}"${slash}>`;
        }
        const width = canvasResolved
            ? Math.max(EMAIL_CANVAS_MAX_WIDTH - calculateImagePadding(html, offset, canvasInnerStart, canvasInnerEnd), 1)
            : WIDTHLESS_IMG_DEFAULT_WIDTH;
        return `<img${attrs} width="${width}"${slash}>`;
    });
};
const SECTION_GHOST_TABLE_OPEN = `<!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;"><![endif]-->`;
const SECTION_GHOST_TABLE_CLOSE = `<!--[if mso]></table><![endif]-->`;
// Wraps one section `<div>` in an `[if mso]><tr><td>` ghost row carrying its bg-color and padding.
const buildSectionRowWrap = (sectionHtml) => {
    const bg = sectionHtml.match(/<div\s[^>]*?style="([^"]*)"/)?.[1];
    const bgColor = bg ? readCssValue(bg, "background-color") : undefined;
    const bgAttr = bgColor ? ` bgcolor="${bgColor}"` : "";
    const bgStyle = bgColor ? `background-color:${bgColor};` : "";
    const { top, right, bottom, left } = parsePadding(bg ?? "");
    const pad = `${top}px ${right}px ${bottom}px ${left}px`;
    return (`<!--[if mso]><tr><td${bgAttr} style="${bgStyle}mso-padding-alt:${pad};"><![endif]-->` +
        sectionHtml +
        `<!--[if mso]></td></tr><![endif]-->`);
};
// Wraps every top-level section `<div>` inside the canvas `<td>` in an `[if mso]><tr><td>` ghost row so Word renders sections as real table cells (prevents inter-`<div>` bg color inconsistencies).
const wrapSectionsForOutlook = (html) => {
    const tdMatch = html.match(CANVAS_INNER_TD_REGEX);
    if (!tdMatch || tdMatch.index === undefined) {
        return html;
    }
    const tdInnerStart = tdMatch.index + tdMatch[0].length;
    const tdEnd = findMatchingClose(html, "td", tdInnerStart);
    if (tdEnd === -1) {
        return html;
    }
    const tdInnerEnd = tdEnd - "</td>".length;
    const sections = findTopLevelDivs(html, tdInnerStart, tdInnerEnd);
    if (sections.length === 0) {
        return html;
    }
    let out = html.slice(0, tdInnerStart) + SECTION_GHOST_TABLE_OPEN;
    let prev = tdInnerStart;
    for (const section of sections) {
        out += html.slice(prev, section.start) + buildSectionRowWrap(html.slice(section.start, section.end));
        prev = section.end;
    }
    return out + html.slice(prev, tdInnerEnd) + SECTION_GHOST_TABLE_CLOSE + html.slice(tdInnerEnd);
};
// Wraps the 600px canvas table in an `[if mso | IE]` ghost table to lock its width in Word.
const wrapCanvasInMsoGhostTable = (html) => {
    const canvasOpenMatch = html.match(/<table[^>]*max-width:\s*600px[^>]*>/i);
    if (!canvasOpenMatch || canvasOpenMatch.index === undefined) {
        return html;
    }
    const canvasStart = canvasOpenMatch.index;
    const canvasEnd = findMatchingClose(html, "table", canvasStart + canvasOpenMatch[0].length);
    if (canvasEnd === -1) {
        return html;
    }
    return (html.slice(0, canvasStart) +
        MSO_CANVAS_GHOST_OPEN +
        html.slice(canvasStart, canvasEnd) +
        MSO_CANVAS_GHOST_CLOSE +
        html.slice(canvasEnd));
};
// Outlook safety wrap for an already-rendered Waypoint body; short-circuits if already wrapped.
export const wrapForOutlook = (html) => {
    if (html.includes(WRAPPED_FOR_OUTLOOK_SENTINEL)) {
        return html;
    }
    const withDoctype = html.replace(/<!DOCTYPE[^>]*>/i, XHTML_DOCTYPE);
    const withHtmlOpen = withDoctype.replace(/<html(\s[^>]*)?>/i, OUTLOOK_HTML_OPEN);
    const withoutPreloads = withHtmlOpen.replace(/<link\s+rel="preload"[^>]*\/?>/g, "");
    const withHeadExtras = withoutPreloads.replace(/<head>/i, `<head>${OUTLOOK_HEAD_EXTRAS}`);
    const withButtons = wrapButtonsForOutlook(withHeadExtras);
    const withSections = wrapSectionsForOutlook(withButtons);
    const withSocials = wrapSocialsBlockForOutlook(withSections);
    const withSpacers = wrapSpacersForOutlook(withSocials);
    const withImgWidth = clampWidthlessImagesToContext(withSpacers);
    const withDividers = wrapDividersForOutlook(withImgWidth);
    return wrapCanvasInMsoGhostTable(withDividers);
};
